import { Box, TextField, CircularProgress } from '@mui/material';
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from '../../../components/Cards/Card';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState } from "react";
import { toast } from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { BASE_URL } from "../../../components/BaseUrl";
import { useNavigate } from 'react-router-dom';
import { setProfileUpdateToken } from "../../../Redux/Slices/AuthSlice";
import { useTranslation } from "react-i18next";
import { setOtpData } from "../../../Redux/Slices/AuthSlice";
import { useLocation } from "react-router-dom";
import { getApiMessage } from '../../../utils/messageHandler';

const OTPScreen = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const verificationCode = otp.join("");
  const email = useSelector((state) => state.auth.otpData?.email);
  const otpToken = useSelector((state) => state.auth.otpData?.otpToken);

  const [showError, setShowError] = useState(false);
  const [timer, setTimer] = useState(15); // ⏳ start from 15
  const [isLoading, setIsLoading] = useState(false); // verify button loader
  const [resendLoading, setResendLoading] = useState(false); // resend button loader
  const inputRefs = useRef([]);

  // ⏳ Countdown effect
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // Handle OTP input change
  const handleChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // 🔄 Resend OTP

// 🔄 Resend OTP
const handleResendOtp = async () => {
  if (!email) {
    toast.error(t("otpScreen.error_missing_email"));
    return;
  }

  setResendLoading(true);
  try {
    const response = await fetch(`${BASE_URL}/public/worker/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    console.log("📩 Resend OTP response:", data);

    if (!response.ok || data.error) {
      console.error("❌ Resend OTP failed:", data);
      toast.error(getApiMessage(data, "Failed to resend OTP"));
      setResendLoading(false);
      return;
    }

    toast.success(getApiMessage(data, "OTP resent successfully"));

    // 🆕 update Redux with new otpToken + verificationCode
    if (data?.data?.otpToken) {
      dispatch(setOtpData({
        email,
        otpToken: data.data.otpToken,
        verificationCode: data.data.verificationCode,
      }));
      console.log("✅ Updated Redux with new OTP token:", data.data.otpToken);
      console.log("✅ New verification code:", data.data.verificationCode);
    }

    // Reset timer & input boxes
    setTimer(15);
    setOtp(["", "", "", "", "", ""]);
  } catch (err) {
    console.error("🔥 Resend OTP error:", err.message);
    const errorMsg = err.response?.data 
      ? getApiMessage(err.response.data, "Something went wrong")
      : (err.message || "Something went wrong");
    toast.error(errorMsg);
  } finally {
    setResendLoading(false);
  }
};
const location = useLocation();
console.log("📦 Location state on OTP screen:", location.state);

const { fromLogin, verificationCode: codeFromLogin, otpToken: tokenFromLogin } = location.state || {};

const handleVerify = async () => {
  let finalCode = verificationCode; // user typed
  let finalToken = otpToken; // redux

  if (fromLogin) {
    console.log("🟢 From login flow detected");
    if (codeFromLogin) {
      console.log("👉 Using verificationCode from Login flow:", codeFromLogin);
      finalCode = codeFromLogin;
    }
    if (tokenFromLogin) {
      console.log("👉 Using otpToken from Login flow:", tokenFromLogin);
      finalToken = tokenFromLogin;
    }
  }

  console.log("📤 Payload to verify API:", { otp_token: finalToken, verification_code: finalCode });

  if (finalCode.length !== 6) {
    setShowError(true);
  toast.error(t("enter_verification_code"));
    return;
  }
  if (!finalToken) {
  toast.error(t("missing_otp_token"));
    return;
  }

  setIsLoading(true);
  try {
    const response = await fetch(`${BASE_URL}/public/worker/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ otp_token: finalToken, verification_code: finalCode }),
    });

    console.log("🌐 Verify response status:", response.status);
    const data = await response.json();
    console.log("📩 Verify API response body:", data);

    if (!response.ok || data.error) {
      toast.error(getApiMessage(data, "Verification failed"));
      setIsLoading(false);
      return;
    }

    const profileToken = data?.data?.profile_token;
    // console.log("🔑 Profile token received:", profileToken);

    if (profileToken) {
      dispatch(setProfileUpdateToken(profileToken));
      localStorage.setItem("profileUpdateToken", profileToken);
    }

    toast.success(getApiMessage(data, "Email verified successfully"));
    navigate("/create-profile");
  } catch (err) {
    console.error("🔥 Verify error:", err.message);
    const errorMsg = err.response?.data 
      ? getApiMessage(err.response.data, "Something went wrong")
      : (err.message || "Something went wrong");
    toast.error(errorMsg);
  } finally {
    setIsLoading(false);
  }
};

const handlePaste = (e) => {
  e.preventDefault();
  const pasteData = e.clipboardData.getData("text").trim();

  // Only accept digits
  if (!/^\d{1,6}$/.test(pasteData)) return;

  // Split digits and fill inputs
  const newOtp = pasteData.split("").slice(0, 6);
  setOtp(newOtp);

  // ✅ Fix: correctly reset error
  if (showError) setShowError(false);

  // Optionally move focus to last filled field
  const lastIndex = newOtp.length - 1;
  if (lastIndex >= 0) inputRefs.current[lastIndex]?.focus();
};

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", pt: isMobile ? 3 : 5 }}>
      <Box sx={{ px: 2, display: 'flex', justifyContent: 'center', minHeight: "100vh", alignItems: "center" }}>
        <BaseCard maxWidth={isMobile ? '100%' : 380}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            
            {/* Title */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
              <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
                {t("otpScreen.enter_otp_title")}
              </CustomText>
              <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, pt: 2 }}>
                {t("otpScreen.enter_otp_description")}
              </CustomText>
            </Box>

            {/* OTP Inputs */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={el => inputRefs.current[index] = el}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                     onPaste={handlePaste} 
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: '24px',
                      width: '45px',
                      height: '30px',
                      borderRadius: '12px',
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: showError && !digit
                          ? 'red'
                          : digit
                          ? theme.palette.primary.main
                          : '#ccc',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              ))}
            </Box>

            {/* Resend Text */}
            <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, textAlign: 'center' }}>
              {t("otpScreen.resend_text")}{" "}
              {timer > 0 ? (
                <span style={{ color: "#0066FF", fontWeight: 600 }}>{timer}s</span>
              ) : (
                <span
                  onClick={!resendLoading ? handleResendOtp : undefined}
                  style={{
                    color: resendLoading ? "#999" : "#0066FF",
                    fontWeight: 600,
                    cursor: resendLoading ? "default" : "pointer",
                  }}
                >
                  {resendLoading ? (
                    <CircularProgress size={16} sx={{ color: "#0066FF" }} />
                  ) : (
                    t("otpScreen.resend_action")
                  )}
                </span>
              )}
            </CustomText>

            {/* Verify Button */}
            <CustomButton
              label={
                isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                    <span>{t("otpScreen.verifying")}</span>
                  </Box>
                ) : (
                  <span>{t("otpScreen.verify_button")}</span>
                )
              }
              onClick={handleVerify}
              fullWidth
              sx={{ mt: 2 }}
            />
          </Box>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default OTPScreen;



// import { Box, TextField, CircularProgress } from '@mui/material';
// import CustomText, { textStyles } from "../../../components/CustomText";
// import CustomButton from "../../../components/CustomButton";
// import BaseCard from '../../../components/Cards/Card';
// import { useTheme } from "@mui/material/styles";
// import useMediaQuery from '@mui/material/useMediaQuery';
// import { useEffect, useRef, useState } from "react";
// import { toast } from 'react-hot-toast';
// import { useSelector, useDispatch } from 'react-redux';
// import { BASE_URL } from "../../../components/BaseUrl";
// import { useNavigate } from 'react-router-dom';
// import { setProfileUpdateToken } from "../../../Redux/Slices/AuthSlice";
// import { useTranslation } from "react-i18next";
// import { setOtpData } from "../../../Redux/Slices/AuthSlice";
// import { useLocation } from "react-router-dom";

// const OTPScreen = () => {
//   const theme = useTheme();
//   const { t } = useTranslation();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [otp, setOtp] = useState(["", "", "", "", "", ""]);
//   const verificationCode = otp.join("");
//   const email = useSelector((state) => state.auth.otpData?.email);
//   const otpToken = useSelector((state) => state.auth.otpData?.otpToken);

//   const [showError, setShowError] = useState(false);
//   const [timer, setTimer] = useState(15); // ⏳ start from 15
//   const [isLoading, setIsLoading] = useState(false); // verify button loader
//   const [resendLoading, setResendLoading] = useState(false); // resend button loader
//   const inputRefs = useRef([]);

//   // ⏳ Countdown effect
//   useEffect(() => {
//     if (timer <= 0) return;
//     const interval = setInterval(() => {
//       setTimer((prev) => prev - 1);
//     }, 1000);
//     return () => clearInterval(interval);
//   }, [timer]);

//   // Handle OTP input change
//   const handleChange = (index, value) => {
//     if (!/^[0-9]?$/.test(value)) return;
//     const newOtp = [...otp];
//     newOtp[index] = value;
//     setOtp(newOtp);
//     if (value && index < 5) inputRefs.current[index + 1]?.focus();
//   };

//   const handleKeyDown = (e, index) => {
//     if (e.key === "Backspace" && !otp[index] && index > 0) {
//       inputRefs.current[index - 1]?.focus();
//     }
//   };

//   // 🔄 Resend OTP

// // 🔄 Resend OTP
// const handleResendOtp = async () => {
//   if (!email) {
//     toast.error(t("otpScreen.error_missing_email"));
//     return;
//   }

//   setResendLoading(true);
//   try {
//     const response = await fetch(`${BASE_URL}/public/worker/resend-verification`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ email }),
//     });

//     const data = await response.json();
//     console.log("📩 Resend OTP response:", data);

//     if (!response.ok || data.error) {
//       console.error("❌ Resend OTP failed:", data);
//       throw new Error(data.message || "Failed to resend OTP");
//     }

//     toast.success(data.message || "OTP resent successfully");

//     // 🆕 update Redux with new otpToken + verificationCode
//     if (data?.data?.otpToken) {
//       dispatch(setOtpData({
//         email,
//         otpToken: data.data.otpToken,
//         verificationCode: data.data.verificationCode,
//       }));
//       console.log("✅ Updated Redux with new OTP token:", data.data.otpToken);
//       console.log("✅ New verification code:", data.data.verificationCode);
//     }

//     // Reset timer & input boxes
//     setTimer(15);
//     setOtp(["", "", "", "", "", ""]);
//   } catch (err) {
//     console.error("🔥 Resend OTP error:", err.message);
//     toast.error(err.message || "Something went wrong");
//   } finally {
//     setResendLoading(false);
//   }
// };

//   // ✅ Verify OTP
// const handleVerify = async () => {
//   console.log("👉 User entered code:", verificationCode);
//   console.log("👉 Current otpToken from Redux:", otpToken);

//   if (verificationCode.length !== 6) {
//     setShowError(true);
//     toast.error("Please enter verification code");
//     console.log("❌ Wrong length, got:", verificationCode.length);
//     return;
//   }
//   if (!otpToken) {
//     toast.error("Missing OTP token from Redux");
//     console.log("❌ Missing otpToken, cannot verify");
//     return;
//   }

//   setIsLoading(true);
//   try {
//     const payload = { otp_token: otpToken, verification_code: verificationCode };
//     console.log("📤 Sending payload to verify API:", payload);

//     const response = await fetch(`${BASE_URL}/public/worker/verify-email`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     console.log("🌐 Verify response status:", response.status);
//     const data = await response.json();
//     console.log("📩 Verify API response:", data);

//     if (!response.ok || data.error) {
//       throw new Error(data.message || "Verification failed");
//     }

//     const profileToken = data?.data?.profile_token;
//     console.log("🔑 Profile token received:", profileToken);

//     if (profileToken) {
//       dispatch(setProfileUpdateToken(profileToken));
//       localStorage.setItem("profileUpdateToken", profileToken);
//       console.log("✅ Profile token saved");
//     }

//     toast.success(data.message || "Email verified successfully");
//     navigate("/create-profile");
//   } catch (err) {
//     console.error("🔥 Verify error:", err.message);
//     toast.error(err.message || "Something went wrong");
//   } finally {
//     setIsLoading(false);
//   }
// };

//   return (
//     <Box sx={{ backgroundColor: "#EFF4F8", pt: isMobile ? 3 : 5 }}>
//       <Box sx={{ px: 2, display: 'flex', justifyContent: 'center', minHeight: "100vh", alignItems: "center" }}>
//         <BaseCard maxWidth={isMobile ? '100%' : 380}>
//           <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            
//             {/* Title */}
//             <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
//               <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
//                 {t("otpScreen.enter_otp_title")}
//               </CustomText>
//               <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, pt: 2 }}>
//                 {t("otpScreen.enter_otp_description")}
//               </CustomText>
//             </Box>

//             {/* OTP Inputs */}
//             <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
//               {otp.map((digit, index) => (
//                 <TextField
//                   key={index}
//                   inputRef={el => inputRefs.current[index] = el}
//                   value={digit}
//                   onChange={(e) => handleChange(index, e.target.value)}
//                   onKeyDown={(e) => handleKeyDown(e, index)}
//                   inputProps={{
//                     maxLength: 1,
//                     style: {
//                       textAlign: 'center',
//                       fontSize: '24px',
//                       width: '45px',
//                       height: '30px',
//                       borderRadius: '12px',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiOutlinedInput-root': {
//                       borderRadius: '12px',
//                       '& fieldset': {
//                         borderColor: showError && !digit
//                           ? 'red'
//                           : digit
//                           ? theme.palette.primary.main
//                           : '#ccc',
//                       },
//                       '&.Mui-focused fieldset': {
//                         borderColor: theme.palette.primary.main,
//                         borderWidth: '2px',
//                       },
//                     },
//                   }}
//                 />
//               ))}
//             </Box>

//             {/* Resend Text */}
//             <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, textAlign: 'center' }}>
//               {t("otpScreen.resend_text")}{" "}
//               {timer > 0 ? (
//                 <span style={{ color: "#0066FF", fontWeight: 600 }}>{timer}s</span>
//               ) : (
//                 <span
//                   onClick={!resendLoading ? handleResendOtp : undefined}
//                   style={{
//                     color: resendLoading ? "#999" : "#0066FF",
//                     fontWeight: 600,
//                     cursor: resendLoading ? "default" : "pointer",
//                   }}
//                 >
//                   {resendLoading ? (
//                     <CircularProgress size={16} sx={{ color: "#0066FF" }} />
//                   ) : (
//                     t("otpScreen.resend_action")
//                   )}
//                 </span>
//               )}
//             </CustomText>

//             {/* Verify Button */}
//             <CustomButton
//               label={
//                 isLoading ? (
//                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                     <CircularProgress size={18} sx={{ color: "#fff" }} />
//                     <span>{t("otpScreen.verifying")}</span>
//                   </Box>
//                 ) : (
//                   <span>{t("otpScreen.verify_button")}</span>
//                 )
//               }
//               onClick={handleVerify}
//               fullWidth
//               sx={{ mt: 2 }}
//             />
//           </Box>
//         </BaseCard>
//       </Box>
//     </Box>
//   );
// };

// export default OTPScreen;





